/**
 * User Management CrossFrame Listeners — Gateway API
 *
 * Proxies CrossFrame requests from the User Management tool (iframe)
 * to JwoStoreManagementGateway via getServiceClient().
 */

import {
    listUsers,
    createUser,
    enableUser,
    disableUser,
    updateUserMetadata,
    updateUserRoleAndStoreAssignment,
    getCurrentUser,
    setupIdentityProvider,
    getServiceProviderUrl,
    listIdentityProviders,
    resendUserInvitation,
} from '@api/userManagement'
import { getUserStores } from '@api/userAndStoreContext'
import { AtCoreServiceClient } from '@api/AtCoreServiceClient'
import { EnvironmentDetail } from '@amzn/ihm-associate-tools-js-lib'

export async function listUsersListener (message: any): Promise<any> {
    const params = message?.data || {}
    const result = await listUsers({
        limit: params.limit,
        paginationToken: params.paginationToken,
    })
    // Convert Coral Timestamp (Date) to epoch seconds for the tool's StoreAssignment.expiresAt field
    const users = (result.users || []).map((u: any) => {
        if (!u.storeAssignments || u.storeAssignments.length === 0) return u
        return {
            ...u,
            storeAssignments: u.storeAssignments.map((a: any) => {
                if (a.expiresAt instanceof Date) {
                    return { ...a, expiresAt: Math.floor(a.expiresAt.getTime() / 1000) }
                }
                return a
            }),
        }
    })
    return { users, paginationToken: result.paginationToken }
}

export async function getCurrentUserListener (_message: any): Promise<any> {
    return getCurrentUser()
}

export async function createUserListener (message: any): Promise<any> {
    const params = message?.data || {}
    return createUser({
        email: params.email,
        name: params.name,
        identityType: params.identityType,
        organizationId: params.organizationId,
    })
}

export async function getUserListener (message: any): Promise<any> {
    const params = message?.data || {}
    if (!params.userId) return null
    let paginationToken: string | undefined
    do {
        const result = await listUsers({ limit: 60, paginationToken })
        const match = (result.users || []).find((u: any) => u.id === params.userId)
        if (match) {
            if (!match.storeAssignments || match.storeAssignments.length === 0) return match
            return {
                ...match,
                storeAssignments: match.storeAssignments.map((a: any) => {
                    if (a.expiresAt instanceof Date) {
                        return { ...a, expiresAt: Math.floor(a.expiresAt.getTime() / 1000) }
                    }
                    return a
                }),
            }
        }
        if (result.paginationToken && result.paginationToken === paginationToken) return null
        paginationToken = result.paginationToken
    } while (paginationToken)
    return null
}

export async function updateMetadataListener (message: any): Promise<any> {
    const params = message?.data || {}
    // organizationId is forwarded for the same reason as the sibling listeners:
    // the Gateway authorizer needs a scope to resolve the caller's policy store.
    return updateUserMetadata({
        userId: params.userId,
        name: params.displayName,
        organizationId: params.organizationId,
    })
}

export async function updateAssignmentsListener (message: any): Promise<any> {
    const params = message?.data || {}
    return updateUserRoleAndStoreAssignment({
        userId: params.userId,
        isAdmin: params.isAdmin,
        storeAssignments: params.storeAssignments,
        organizationId: params.organizationId,
        assignments: params.assignments,
    })
}

export async function enableUserListener (message: any): Promise<any> {
    const params = message?.data || {}
    return enableUser({ userId: params.userId, organizationId: params.organizationId })
}

export async function disableUserListener (message: any): Promise<any> {
    const params = message?.data || {}
    return disableUser({ userId: params.userId, organizationId: params.organizationId })
}

export async function listAllStoresListener (_message: any): Promise<any> {
    const storeId = EnvironmentDetail.getStoreId()
    const userAlias = await AtCoreServiceClient.getInstance().getUserAlias(storeId)
    const stores = await getUserStores(userAlias)
    return stores.map((s) => ({
        storeId: s.storeId,
        storeName: s.storeDetails?.storeName ?? s.storeId,
    }))
}

// ---------------------------------------------------------------------------
// IDP management listeners
// ---------------------------------------------------------------------------

export async function setupIdentityProviderListener (message: any): Promise<any> {
    const params = message?.data || {}
    return setupIdentityProvider({
        providerName: params.providerName,
        providerDetails: params.providerDetails,
        attributeMapping: params.attributeMapping,
        idpIdentifiers: params.idpIdentifiers,
        organizationId: params.organizationId,
    })
}

export async function getServiceProviderUrlListener (message: any): Promise<any> {
    const params = message?.data || {}
    return getServiceProviderUrl({ organizationId: params.organizationId })
}

export async function listIdentityProvidersListener (message: any): Promise<any> {
    const params = message?.data || {}
    return listIdentityProviders({
        maxResults: params.maxResults,
        nextToken: params.nextToken,
        organizationId: params.organizationId,
    })
}

export async function resendUserInvitationListener (message: any): Promise<any> {
    const params = message?.data || {}
    return resendUserInvitation({ userId: params.userId, organizationId: params.organizationId })
}
